/**
 * Contrato de `/admin/aristo-medq/guarantee-campaigns` (monolith,
 * `guarantee-campaign-output.dto.ts`). Ver docs/reference/modules/aristo-medq.md
 * § A tela de campanhas.
 */

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  perPage: number;
}

/** A régua que a campanha promete — dado, não código. */
export interface GuaranteeRule {
  durationWeeks: number;
  weeklyTarget: number;
  dailyCap: number;
  minDaysPerWeek: number;
  /** Fração das semanas que precisam fechar a meta, em (0, 1]. */
  eligibilityRatio: number;
}

export interface GuaranteeCampaignListItem {
  id: string;
  name: string;
  offerCount: number;
  /** Garantias de pé agora — não canceladas e ainda dentro do prazo. */
  activeGuarantees: number;
  createdAt: string;
  updatedAt: string;
}

export interface GuaranteeCampaignOffer {
  providerAccountId: string;
  offerExternalId: string;
  /** Id na `sales_offers`, destino do link; `null` junto com o `name`. */
  offerId: string | null;
  /** `null` quando a oferta saiu da `sales_offers` depois de entrar aqui. */
  name: string | null;
  productName: string | null;
  providerAccountName: string | null;
}

export interface GuaranteeCampaignDetail {
  id: string;
  name: string;
  rule: GuaranteeRule;
  offers: GuaranteeCampaignOffer[];
  activeGuarantees: number;
  /**
   * Vem do back, não se deduz de `activeGuarantees`: garantia encerrada sem
   * cancelamento também trava (o aluno ainda pode pedir a análise).
   */
  ruleLocked: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CampaignOfferRef {
  providerAccountId: string;
  offerExternalId: string;
}

export type GuaranteeRuleField = keyof GuaranteeRule;
export type GuaranteeRuleErrors = Partial<Record<GuaranteeRuleField, string>>;

/**
 * Espelho de `assertValidRule` da entidade `GuaranteeCampaign`. É só UX: quem
 * recusa de verdade é o back.
 */
export function validateGuaranteeRule(
  rule: GuaranteeRule,
): GuaranteeRuleErrors {
  const errors: GuaranteeRuleErrors = {};
  const positiveInts = [
    'durationWeeks',
    'weeklyTarget',
    'dailyCap',
    'minDaysPerWeek',
  ] as const;
  for (const field of positiveInts) {
    if (!Number.isInteger(rule[field]) || rule[field] < 1) {
      errors[field] = 'Use um número inteiro a partir de 1.';
    }
  }
  if (!errors.minDaysPerWeek && rule.minDaysPerWeek > 7) {
    errors.minDaysPerWeek = 'A semana tem 7 dias.';
  }
  if (
    !Number.isFinite(rule.eligibilityRatio) ||
    rule.eligibilityRatio <= 0 ||
    rule.eligibilityRatio > 1
  ) {
    errors.eligibilityRatio = 'Use um valor maior que 0 e até 1 (ex.: 0,8).';
  }
  return errors;
}
